import { FC, useMemo, useState } from 'react';
import Page, { PageProps } from '../../components/page';
import { createUseStyles } from 'react-jss';
import useHome from '@/hooks/use-home.hook';
import { HomeProvider } from '@/contexts/home';
import PageContent from '@/components/page/content';
import TileGroup from '@/components/tile-group';
import TileGroupHeader from '@/components/tile-group/header';
import { UiOption } from '@/lib/helpers';
import TileContent from '@/components/tile-group/content';
import { TILE_COMPONENTS } from '@/components/tile-group/tile/helpers';
import EmptyTile from '@/components/tile-group/empty-tile';
import { orderBy } from 'lodash';

const useStyles = createUseStyles({
  catalogPage: {
    overflowY: 'auto',
  },
});

type QuickFilterId = 'agent' | 'public-service' | 'favorite' | 'available';

const CatalogPage: FC<PageProps> = (props) => {
  return (
    <HomeProvider>
      <CatalogPageWithContext {...props} />
    </HomeProvider>
  );
};

const CatalogPageWithContext: FC<PageProps> = () => {
  const styles = useStyles();
  const { tiles, hasAgents, hasServices, publicServiceTiles } = useHome();
  const [keyword, setKeyword] = useState('');
  const [quickFilters, setQuickFilters] = useState<QuickFilterId[]>([]);

  const handleChangeQuickFilters = (filterId: QuickFilterId, enabled: boolean) => {
    setQuickFilters((old) => {
      if (enabled) {
        return [...old, filterId];
      }
      return old.filter((val) => val !== filterId);
    });
  };

  const quickFilterOptions = useMemo(() => {
    const options: UiOption[] = [
      { label: 'My Favorites', value: 'favorite' },
      { label: 'Available', value: 'available' },
    ];

    if (hasAgents) {
      options.push({ label: 'Assistants', value: 'agent' });
    }

    if (hasServices) {
      options.push({ label: 'Apps', value: 'public-service' });
    }

    return options;
  }, [hasAgents, hasServices]);

  const filterTiles = useMemo(() => {
    const safeKeyword = String(keyword).trim().toLowerCase();

    const allTiles = [
      ...tiles.filter(({ tileType }) => tileType === 'agent'),
      ...publicServiceTiles,
    ];

    const filterTiles = allTiles.filter(({ keywords, tileType, favorite, hasAccess }) => {
      if (safeKeyword) {
        // keyword filter
        const keywordMatch = keywords.some((field) =>
          String(field).toLowerCase().includes(safeKeyword)
        );

        if (!keywordMatch) {
          return false;
        }
      }

      if (quickFilters.length) {
        const decoratedFilters = quickFilters.filter((value) =>
          ['available', 'favorite'].includes(value)
        );

        const typeFilters = quickFilters.filter((value) =>
          ['agent', 'public-service'].includes(value)
        );

        const filterMatches = (filter: QuickFilterId) => {
          switch (filter) {
            case 'available':
              return hasAccess || tileType === 'agent';
            case 'favorite':
              return favorite;
            case 'agent':
              return tileType === 'agent';
            case 'public-service':
              return tileType === 'public-service';
          }
        };

        if (decoratedFilters.length && !decoratedFilters.some(filterMatches)) {
          return false;
        }

        if (typeFilters.length && !typeFilters.some(filterMatches)) {
          return false;
        }
      }

      return true;
    });

    return orderBy(filterTiles, ({ keywords }) => String(keywords[0] || '').toLowerCase(), ['asc']);
  }, [tiles, publicServiceTiles, keyword, quickFilters]);

  const filterActive = !!keyword || !!quickFilters.length;
  const showEmptyTile = filterActive && !filterTiles.length;

  return (
    <Page className={styles.catalogPage} title="All Apps">
      <PageContent>
        <TileGroup>
          <TileGroupHeader
            title="All Apps"
            icon="catalog"
            onSearch={setKeyword}
            onChangeQuickFilters={handleChangeQuickFilters}
            quickFilters={quickFilters}
            quickFilterOptions={quickFilterOptions}
          />
          <TileContent>
            {filterTiles.map((tile) => {
              const TileEl = TILE_COMPONENTS[tile.tileType];

              if (TileEl) {
                return (
                  <TileEl
                    key={tile.tileId}
                    tile={tile}
                    showAccessIcon={tile.hasAccess}
                  />
                );
              }
            })}
          </TileContent>
        </TileGroup>
        {showEmptyTile && <EmptyTile />}
      </PageContent>
    </Page>
  );
};

export default CatalogPage;
